"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { MessageSquare, Send } from "lucide-react";
import { useTranslations } from "next-intl";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";

export default function CarListingInquiryButton({
  carId,
  carTitle,
  isLoggedIn,
  loginRedirect,
}: {
  carId: string;
  carTitle: string;
  isLoggedIn: boolean;
  loginRedirect: string;
}) {
  const t = useTranslations("CarDetailPage");
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const text = message.trim();
    if (!text) {
      setError(t("inquiryErrorEmpty"));
      return;
    }
    setLoading(true);
    setError("");
    const res = await fetch("/api/car-threads", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ car_id: carId, message: text }),
    });
    const data = await res.json().catch(() => ({}));
    setLoading(false);
    if (!res.ok) {
      setError(typeof data.error === "string" ? data.error : t("inquiryErrorSend"));
      return;
    }
    setSent(true);
    setMessage("");
    router.refresh();
    setTimeout(() => {
      setOpen(false);
      setSent(false);
    }, 1600);
  }

  const buttonClass =
    "flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-2.5 text-sm font-medium text-gray-800 shadow-sm transition-colors hover:border-[#E63946]/35 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-100 dark:hover:bg-gray-800/60";

  if (!isLoggedIn) {
    return (
      <Link href={loginRedirect} className={buttonClass}>
        <MessageSquare size={16} className="flex-shrink-0 text-[#E63946]" />
        {t("inquiryButton")}
      </Link>
    );
  }

  return (
    <>
      <button type="button" onClick={() => { setOpen(true); setError(""); setSent(false); }} className={buttonClass}>
        <MessageSquare size={16} className="flex-shrink-0 text-[#E63946]" />
        {t("inquiryButton")}
      </button>

      <Modal open={open} onClose={() => !loading && setOpen(false)} title={t("inquiryModalTitle")} size="md">
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t("inquiryModalIntro", { car: carTitle })}
          </p>
          {sent ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
              {t("inquirySent")}
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                placeholder={t("inquiryPlaceholder")}
                className="w-full resize-none rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#E63946] dark:border-gray-600 dark:bg-gray-950 dark:text-gray-100"
              />
              {error && (
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              )}
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setOpen(false)} disabled={loading}>
                  {t("inquiryCancel")}
                </Button>
                <Button type="submit" className="flex-1 gap-2" loading={loading}>
                  <Send size={14} />
                  {t("inquirySend")}
                </Button>
              </div>
            </form>
          )}
        </div>
      </Modal>
    </>
  );
}
